import {
  TIPO_INTERFAZ_CONECTAR,
  WS_CLIENT_TYPE,
  WS_EVENTS,
  WsAudioBackchannelGeneradoPorPacientePayloadSchema,
  WsAudioBinarioMetaSchema,
  WsConectarAckSchema,
  WsPacienteHablandoFinPayloadSchema,
  WsPacienteHablandoInicioPayloadSchema,
  WsSesionErrorPayloadSchema,
  WsTurnoFallidoPayloadSchema,
  type WsAudioBackchannelGeneradoPorPacientePayload,
  type WsAudioBinarioPayload,
  type WsAudioGeneradoPorAlumnoPayload,
  type WsAudioGeneradoPorAlumnoWire,
  type WsConectarAck,
  type WsConectarPayload,
  type WsHandshakeAuth,
  type WsPacienteHablandoFinPayload,
  type WsPacienteHablandoInicioPayload,
  type WsSesionErrorPayload,
  type WsTurnoFallidoPayload,
} from '@alter/types';
import { io as defaultIo, type Socket } from 'socket.io-client';
import type { z } from 'zod';
import type { TokenStorage } from './tokenStorage.js';
import {
  modalidadParaConectar,
  WsAudioGeneradoPorPacienteRemappedSchema,
  type WsAudioGeneradoPorPacienteRemapped,
} from './wsRemap.js';

/** Factory inyectable de `io` para tests. Default: `socket.io-client`. */
export type IoFactory = typeof defaultIo;

/**
 * Tope de espera del `disconnect` que inicia el servidor tras
 * `sesion.finalizada_por_alumno` (nota de interfaz §6.3).
 */
export const CIERRE_TIMEOUT_MS = 3000;

/**
 * Reconexión automática: intentos y espera máxima entre ellos. Con espera
 * inicial de 1 s duplicándose hasta 5 s, la ventana total ronda los 45 s
 * (más el jitter de Socket.IO).
 *
 * Hasta 2026-09-19 eran 5 intentos con espera máxima de 10 s, unos 25 s:
 * en la prueba desde `sp.mvp`, apagar el wifi 10 s más lo que tarda el
 * sistema en volver a asociarse agotó los intentos antes de que hubiera
 * red. Una ventana más larga y reintentos más seguidos cubren ese corte.
 */
export const RECONEXION_INTENTOS = 10;
export const RECONEXION_ESPERA_MAX_MS = 5000;

export interface WebSocketClientConfig {
  /** Base URL del WS, ej. `"https://ws.mvp.alterrealitas.com"`. Sin path. */
  wsBaseUrl: string;
  /** Storage del JWT. Se lee en cada handshake, incluidas las reconexiones. */
  tokenStorage: TokenStorage;
  /** Override de `io` para tests. */
  ioFactory?: IoFactory;
}

interface ConnectArgsComunes {
  tenantId: string;
  /**
   * La modalidad **tal como la mandó el servidor** (`iniciar.modalidad` o
   * `mi-simulacion.simulacion.modalidad`). El wrapper la pasa a minúsculas
   * para `conectar`; el caller no la transforma.
   */
  modalidad: string;
}

/**
 * Args de `connect()`. Dos formas, como `conectar` (JC §0.3):
 *
 * - `ticketRef`: sesión nueva, con el ticket de `POST /iniciar`.
 * - `simulacionId`: reanudar una simulación en curso
 *   (`mi-simulacion.reconexion === true`), sin ticket.
 *
 * Las reconexiones internas de Socket.IO las maneja el wrapper solo: tras
 * el primer ack reemite `conectar` con `simulacion_id` y sin `ticket_ref`.
 */
export type ConnectArgs = ConnectArgsComunes & ({ ticketRef: string } | { simulacionId: string });

/** Issue de validación sin el payload crudo (D-6): solo ruta y mensaje. */
export interface ValidationIssue {
  path: string;
  message: string;
}

/** Resultado de `closeSession()`. */
export interface CloseSessionResult {
  /**
   * `true` si el servidor cerró el socket dentro del tope. `false` si venció
   * el tope y el cliente cortó: el cierre técnico pudo haberse perdido
   * (historial y fase quedan en Redis hasta su TTL de 4 h).
   */
  cerradoPorServidor: boolean;
}

/**
 * Eventos que el wrapper expone al caller. `audio_paciente` ya viene con
 * el remapeo `expresion_vr → expresion_avatar` aplicado.
 */
export interface WebSocketClientEvents {
  // Lifecycle
  connected: (ack: WsConectarAck) => void;
  disconnected: (reason: string) => void;
  reconnection_failed: () => void;

  // Server payloads (validados, post-remap)
  audio_paciente: (payload: WsAudioGeneradoPorPacienteRemapped) => void;
  audio_binario: (payload: WsAudioBinarioPayload) => void;
  audio_backchannel: (payload: WsAudioBackchannelGeneradoPorPacientePayload) => void;
  paciente_hablando_inicio: (payload: WsPacienteHablandoInicioPayload) => void;
  paciente_hablando_fin: (payload: WsPacienteHablandoFinPayload) => void;
  /** Falla de este turno (JC §6.2). No reconecta: el alumno repite el turno. */
  turno_fallido: (payload: WsTurnoFallidoPayload) => void;
  /** Error fatal (JC §0.5). El servidor cierra el socket a continuación. */
  sesion_error: (payload: WsSesionErrorPayload) => void;

  /**
   * Un evento del servidor no parseó. **Nunca incluye el payload crudo**
   * (D-6): hoy el turno del paciente trae `behavioral_cues`, que no debe
   * llegar a ningún log de producto.
   */
  validation_error: (eventName: string, issues: ValidationIssue[]) => void;
}

type AnyListener = (...args: never[]) => void;

/**
 * Wrapper tipado sobre `socket.io-client` v4, alineado con
 * `JSON_CONTRACTS.md` v1.9.0:
 *
 * - Handshake con `auth: { token, client_type: "webxr" }`, token pelado
 *   (JC §0.4). Sin `extraHeaders`: con transporte `websocket` puro el
 *   navegador no los envía en el upgrade.
 * - `conectar` en snake_case con `tipo_interfaz: "web_client"`; en
 *   reconexión, `simulacion_id` y sin `ticket_ref` (JC §0.3).
 * - Backoff exponencial nativo de Socket.IO: `RECONEXION_INTENTOS` intentos,
 *   con espera de 1 a `RECONEXION_ESPERA_MAX_MS` entre ellos.
 * - Validación Zod de cada payload del servidor; falla → `validation_error`.
 * - Cierre técnico que espera el `disconnect` del servidor (`closeSession`).
 *
 * Sin reconexión con ticket nuevo: al agotar los intentos
 * dispara `reconnection_failed` y deja al caller decidir.
 */
export class WebSocketClient {
  private socket: Socket | null = null;
  private connectArgs: ConnectArgs | null = null;
  private confirmedSimulacionId: string | null = null;
  private readonly listeners: Map<keyof WebSocketClientEvents, Set<AnyListener>> = new Map();

  constructor(private readonly config: WebSocketClientConfig) {}

  /**
   * Suscribe un listener al evento. Devuelve función de unsuscribe.
   * Múltiples listeners por evento se ejecutan en orden de registro.
   */
  on<E extends keyof WebSocketClientEvents>(
    event: E,
    callback: WebSocketClientEvents[E],
  ): () => void {
    let set = this.listeners.get(event);
    if (!set) {
      set = new Set();
      this.listeners.set(event, set);
    }
    const cb = callback as unknown as AnyListener;
    set.add(cb);
    return () => {
      set?.delete(cb);
    };
  }

  /**
   * Abre el socket y registra los listeners. Lanza si no hay JWT: sin token
   * el gateway responde `sesion.error: no_autenticado` y cierra. Los eventos
   * de lifecycle (`connected`, `disconnected`, …) llegan vía `on()`.
   */
  async connect(args: ConnectArgs): Promise<void> {
    if (this.socket !== null) {
      throw new Error('WebSocketClient.connect() called while already connected');
    }
    if ((await this.config.tokenStorage.get()) === null) {
      throw new Error('WebSocketClient.connect() requires a JWT in TokenStorage');
    }
    this.connectArgs = args;
    this.confirmedSimulacionId = null;

    const factory = this.config.ioFactory ?? defaultIo;
    const socket = factory(this.config.wsBaseUrl, {
      transports: ['websocket'],
      reconnection: true,
      reconnectionAttempts: RECONEXION_INTENTOS,
      reconnectionDelay: 1000,
      reconnectionDelayMax: RECONEXION_ESPERA_MAX_MS,
      // Función y no objeto: Socket.IO la invoca en cada handshake, así una
      // reconexión usa el token vigente y no el del primer intento.
      auth: (cb: (data: WsHandshakeAuth) => void) => {
        void this.config.tokenStorage.get().then((token) => {
          cb({ token: token ?? '', client_type: WS_CLIENT_TYPE });
        });
      },
    });
    this.socket = socket;

    socket.on('connect', () => this.handleConnect());
    socket.on('disconnect', (reason: string) => this.emit('disconnected', reason));
    // `reconnect_failed` lo emite el Manager (`socket.io`), **no** el socket:
    // escucharlo en el socket no dispara nunca, y la pantalla quedaba en
    // "Reconectando…" para siempre (prueba desde `sp.mvp`, 2026-09-19).
    socket.io.on('reconnect_failed', this.onReconnectFailed);

    this.registerServerListeners(socket);
  }

  private readonly onReconnectFailed = (): void => {
    this.emit('reconnection_failed');
  };

  /** Cierra el socket sin cierre técnico y limpia estado interno. Idempotente. */
  disconnect(): void {
    if (this.socket) {
      // `io()` reutiliza el Manager por URL: sin soltar el listener, cada
      // "Reintentar" sumaría otro y `reconnection_failed` llegaría repetido.
      this.socket.io.off('reconnect_failed', this.onReconnectFailed);
      this.socket.disconnect();
      this.socket = null;
    }
    this.connectArgs = null;
    this.confirmedSimulacionId = null;
  }

  // ---------- Emits cliente → servidor ----------

  /**
   * Turno del alumno (JC §2). Se emite **un solo objeto**: la metadata y el
   * binario en su campo `audio`, que Socket.IO manda como attachment
   * binario. Nunca como dos argumentos: el gateway lee un único
   * `@MessageBody()` y NestJS entregaría un array, con el audio vacío
   * (verificado contra MVP el 2026-09-19). **Sin ack** ni callback: la
   * señal de que el turno entró es `paciente.hablando_inicio`.
   */
  emitAudioGeneradoPorAlumno(
    metadata: Omit<WsAudioGeneradoPorAlumnoPayload, 'evento'>,
    binary: ArrayBuffer,
  ): void {
    const payload: WsAudioGeneradoPorAlumnoWire = {
      evento: 'audio.generado_por_alumno',
      ...metadata,
      audio: binary,
    };
    this.requireSocket().emit(WS_EVENTS.audio_generado_por_alumno, payload);
  }

  /** Fin de la fase de entrevista (JC §8, paso 1). Obligatorio siempre. */
  emitEntrevistaFinalizadaPorAlumno(): void {
    this.requireSocket().emit(WS_EVENTS.entrevista_finalizada_por_alumno, {
      evento: WS_EVENTS.entrevista_finalizada_por_alumno,
    });
  }

  /**
   * Cierre **técnico** (JC §8, paso 6; nota de interfaz §6.3). Emite
   * `sesion.finalizada_por_alumno` y espera el `disconnect` que inicia el
   * servidor, con tope de `CIERRE_TIMEOUT_MS`, antes de liberar el socket.
   * El evento no tiene ack: si el cliente cortara en el mismo instante del
   * `emit`, el paquete se descartaría y el cierre se perdería sin rastro.
   *
   * No reemplaza al cierre de **negocio** (`POST /api/simulacion/finalizar`).
   */
  async closeSession(): Promise<CloseSessionResult> {
    const socket = this.requireSocket();
    const cerradoPorServidor = await new Promise<boolean>((resolve) => {
      const onDisconnect = (): void => {
        clearTimeout(timer);
        socket.off('disconnect', onDisconnect);
        resolve(true);
      };
      const timer = setTimeout(() => {
        socket.off('disconnect', onDisconnect);
        resolve(false);
      }, CIERRE_TIMEOUT_MS);
      socket.on('disconnect', onDisconnect);
      socket.emit(WS_EVENTS.sesion_finalizada_por_alumno, {
        evento: WS_EVENTS.sesion_finalizada_por_alumno,
      });
    });
    this.disconnect();
    return { cerradoPorServidor };
  }

  // ---------- Internal ----------

  private requireSocket(): Socket {
    if (!this.socket) {
      throw new Error('WebSocketClient is not connected — call connect() first');
    }
    return this.socket;
  }

  /** Arma `conectar`: ticket en la primera conexión, `simulacion_id` después. */
  private buildConectar(args: ConnectArgs): WsConectarPayload {
    const comun = {
      evento: 'conectar' as const,
      tenant_id: args.tenantId,
      modalidad: modalidadParaConectar(args.modalidad),
      tipo_interfaz: TIPO_INTERFAZ_CONECTAR,
    };
    if (this.confirmedSimulacionId !== null) {
      return { ...comun, simulacion_id: this.confirmedSimulacionId };
    }
    if ('simulacionId' in args) {
      return { ...comun, simulacion_id: args.simulacionId };
    }
    return { ...comun, ticket_ref: args.ticketRef };
  }

  private handleConnect(): void {
    if (!this.socket || !this.connectArgs) return;
    this.socket.emit(WS_EVENTS.conectar, this.buildConectar(this.connectArgs), (ack: unknown) => {
      const result = WsConectarAckSchema.safeParse(ack);
      if (result.success) {
        this.confirmedSimulacionId = result.data.simulacion_id;
        this.emit('connected', result.data);
      } else {
        this.emit('validation_error', WS_EVENTS.conectar, resumirIssues(result.error));
      }
    });
  }

  private registerServerListeners(socket: Socket): void {
    this.bindValidated(
      socket,
      WS_EVENTS.audio_generado_por_paciente,
      WsAudioGeneradoPorPacienteRemappedSchema,
      'audio_paciente',
    );
    this.bindValidated(
      socket,
      WS_EVENTS.audio_backchannel_generado_por_paciente,
      WsAudioBackchannelGeneradoPorPacientePayloadSchema,
      'audio_backchannel',
    );
    this.bindValidated(
      socket,
      WS_EVENTS.paciente_hablando_inicio,
      WsPacienteHablandoInicioPayloadSchema,
      'paciente_hablando_inicio',
    );
    this.bindValidated(
      socket,
      WS_EVENTS.paciente_hablando_fin,
      WsPacienteHablandoFinPayloadSchema,
      'paciente_hablando_fin',
    );
    this.bindValidated(
      socket,
      WS_EVENTS.turno_fallido,
      WsTurnoFallidoPayloadSchema,
      'turno_fallido',
    );
    this.bindValidated(socket, WS_EVENTS.sesion_error, WsSesionErrorPayloadSchema, 'sesion_error');
    socket.on(WS_EVENTS.audio_binario, (raw: unknown) => this.handleAudioBinario(raw));
  }

  /**
   * Suscribe un listener de Socket.IO que valida el payload con `schema` y
   * dispara `target` en éxito o `validation_error` en falla. `schema` se
   * tipa como `ZodTypeAny` porque algunos schemas tienen `.transform()`.
   */
  private bindValidated<E extends keyof WebSocketClientEvents>(
    socket: Socket,
    eventName: string,
    schema: z.ZodTypeAny,
    target: E,
  ): void {
    socket.on(eventName, (raw: unknown) => {
      const result = schema.safeParse(raw);
      if (result.success) {
        this.emit(target, result.data as Parameters<WebSocketClientEvents[E]>[0]);
      } else {
        this.emit('validation_error', eventName, resumirIssues(result.error));
      }
    });
  }

  /**
   * Frame `audio.binario` (`{ data, mime_type }`, JC §0.2). El `ArrayBuffer`
   * no se valida con Zod; el `mime_type` sí, como string. Si el cliente
   * puede reproducirlo lo decide el reproductor.
   */
  private handleAudioBinario(raw: unknown): void {
    if (
      typeof raw !== 'object' ||
      raw === null ||
      !('data' in raw) ||
      !(raw.data instanceof ArrayBuffer)
    ) {
      this.emit('validation_error', WS_EVENTS.audio_binario, [
        { path: 'data', message: 'Expected ArrayBuffer' },
      ]);
      return;
    }
    const meta = WsAudioBinarioMetaSchema.safeParse(raw);
    if (!meta.success) {
      this.emit('validation_error', WS_EVENTS.audio_binario, resumirIssues(meta.error));
      return;
    }
    this.emit('audio_binario', { data: raw.data, mime_type: meta.data.mime_type });
  }

  /**
   * Despacho interno. La firma usa `unknown[]` porque unificar
   * `Parameters<WebSocketClientEvents[E]>` con tuplas concretas pelea con el
   * contrato genérico de `bindValidated`. La superficie pública (`on`) sigue
   * tipada estrictamente.
   */
  private emit(event: keyof WebSocketClientEvents, ...args: unknown[]): void {
    const set = this.listeners.get(event);
    if (!set) return;
    for (const cb of set) {
      (cb as unknown as (...a: unknown[]) => void)(...args);
    }
  }
}

/** Reduce un `ZodError` a ruta + mensaje, sin valores recibidos (D-6). */
function resumirIssues(error: z.ZodError): ValidationIssue[] {
  return error.issues.map((issue) => ({ path: issue.path.join('.'), message: issue.message }));
}
